/**
 * Canonical URL and hreflang alternates of a page, per language treatment.
 *
 * The app serves every route under two locales (`it` by default, `en` under a
 * `/en` prefix), but the two are not the same kind of pair, and until now they
 * were treated as one: the layout emitted the `it`/`en`/`x-default` alternates
 * on every page while the venue showcase declared a canonical pointing at the
 * Italian version. Those two signals contradict each other. A canonical says
 * "this page is a duplicate, index the other one"; an hreflang says "this page
 * is an autonomous variant, index it and show it to English speakers". Google
 * requires hreflang sets to be reciprocal and self-canonical, so the pair was
 * simply discarded.
 *
 * Hence two explicit modes, decided per route by the page that knows what its
 * content is, never inferred here:
 *
 * - `consolidated` — the main content comes from the client (a venue showcase,
 *   a job ad) and stays Italian whatever language the interface is in. The
 *   English URL keeps a canonical to the Italian one and **neither version**
 *   emits alternates. Dropping them only on the English side would leave a
 *   one-directional set, which Google ignores anyway.
 * - `localized` (default) — our own pages, whose interface and copy do follow
 *   the locale. Every version is self-canonical and declares the whole set of
 *   alternates, identical on each version so the set is reciprocal.
 *
 * An explicit canonical is honoured only in `consolidated` mode: in `localized`
 * a page that consolidated elsewhere would recreate exactly the contradiction
 * above, so the self URL wins.
 */

export type LanguageSeoMode = 'localized' | 'consolidated';

export type LanguageAlternate = {
  hrefLang: string;
  href: string;
};

export type LanguageLinks = {
  canonical: string;
  alternates: LanguageAlternate[];
};

export type LanguageLinksSource = {
  mode?: LanguageSeoMode;
  /** `router.asPath`. With i18n Next strips the locale prefix from it. */
  path?: string;
  /**
   * The path this page consolidates onto when it is not the one being served:
   * the category catch-all answers any depth, and `/cerco-lavoro/a/b/c` has to
   * keep pointing at `/cerco-lavoro` instead of claiming to be its own page.
   * Locale-free, like `path`.
   */
  canonicalPath?: string;
  baseUrl: string;
  locale?: string;
  defaultLocale?: string;
  locales?: readonly string[];
  /** Only used by `consolidated`, where it names the version to index. */
  canonical?: string;
};

// The default locale is served without a prefix, so its URL is the bare path.
const X_DEFAULT = 'x-default';

export function buildLanguageLinks({
  mode = 'localized',
  path,
  canonicalPath,
  baseUrl,
  locale,
  defaultLocale,
  locales,
  canonical,
}: LanguageLinksSource): LanguageLinks {
  // The alternates follow the consolidated path too: an hreflang set has to
  // name canonical URLs, otherwise it points at pages that disown it.
  const cleanPath = normalizePath(canonicalPath ?? path);
  const urlFor = (target?: string) => `${baseUrl}${localePrefix(target, defaultLocale)}${cleanPath}`;

  if (mode === 'consolidated') {
    return { canonical: canonical || urlFor(defaultLocale), alternates: [] };
  }

  // The set is built from the configured locales alone, never from the one
  // being rendered: derived from `locale` it would come out different on each
  // version, which is the one thing an hreflang set cannot be.
  const available = (locales ?? []).filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);
  const alternates = available.map((target) => ({ hrefLang: target, href: urlFor(target) }));
  if (alternates.length > 1 && defaultLocale && available.includes(defaultLocale)) {
    alternates.push({ hrefLang: X_DEFAULT, href: urlFor(defaultLocale) });
  }

  return { canonical: urlFor(locale), alternates };
}

function localePrefix(target?: string, defaultLocale?: string): string {
  return target && target !== defaultLocale ? `/${target}` : '';
}

// The query string and the fragment never belong to a canonical URL, and a
// trailing slash would make the same page look like two different ones next to
// the alternates, which are built without it.
function normalizePath(path?: string): string {
  const withoutQuery = (path || '/').split('?')[0].split('#')[0];
  if (withoutQuery === '/' || withoutQuery === '') return '';
  return withoutQuery.endsWith('/') ? withoutQuery.slice(0, -1) : withoutQuery;
}
